import { yupResolver } from "@hookform/resolvers/yup";
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, Dialog, DialogContent,Grid} from '@mui/material';
import React, { FC, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { AverageAndMedianRemunerationService } from '../../../../Component/Api/EmploymentEquity/AverageAndMedianRemuneration/AverageAndMedianRemunerationService';
import { MDBox } from '../../../../Component/Common/Components/MDBox';
import { MDButton } from '../../../../Component/Common/Components/MDButton';
import { MDTypography } from '../../../../Component/Common/Components/MDTypography';
import { EEAverageAndMedianRemuneration } from '../../../../Component/Model/Dto/EmploymentEquity/AverageAndMedianRemuneration/AverageAndMedianRemuneration';
import { CustomDialogTitle } from '../../../Common/Custom/CustomDialogTitle';
import './AverageAndMedianRemuneration.css';
import { MedianRemunerationEarnerPopup } from './MedianRemunerationEarnerPopup';
import { CurrencyFormatter } from "../../../../Helpers/CurrencyFormatter";
import { otherReasonForIncomeDifferentialsValidationSchema } from "./AverageAndMedianRemunerationValidation";
import { RemunerationDrilldownType } from "../../../../Component/Model/Enum/RemunerationDrilldownType";
import { incomeDifferentialsOptions, YesNoList } from "../../../../Component/Common/Constants/Lists/DropdownListOptions";
import AppSelectList from "../../../../Component/Common/Components/FormValidationInputs/AppSelectList";
import AppTextInput from "../../../../Component/Common/Components/FormValidationInputs/AppTextInput";

interface IProps {
    scorecardId: string;
}

export const buttonTextLinkStyle = {
    textTransform: 'none',
    backgroundColor: 'transparent',  
    color: '#000',
    textDecoration: 'underline',
    justifyContent: 'flex-end',
    fontSize: '0.875rem', 
    fontFamily: 'inherit',
    paddingRight: '5px',
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: '4px',  
    height: '40px',
    fontWeight: '400',
    '&:hover': {
        textDecoration: 'underline',
        color: '#000',
        },
    '&:active': {
        color: '#000',
        },
    '&:focus': {
    color: '#000',
    },
};

export const AverageAndMedianRemuneration: FC<IProps> = (props) => {
    const [previewPopup, setPreviewPopup] = React.useState<boolean>(false);
    const [isTop5PercentEarner, setTop5PercentEarner] = React.useState<boolean>(false);
    const [drilldownType, setDrilldownType] = React.useState<RemunerationDrilldownType>(RemunerationDrilldownType.Remuneration);
    const [isUpperRange, setIsUpperRange] = React.useState<boolean>(false);
    const [reasonForIncomeDifferentials, setReasonForIncomeDifferentials] = React.useState<number>();

    const {handleSubmit ,control, reset, getValues, setValue } = useForm({
        mode: 'all',
        resolver: yupResolver(otherReasonForIncomeDifferentialsValidationSchema(50)),
    });

    const handleClickTopOrBottom = (isTopEarner: boolean, drilldownType: RemunerationDrilldownType, isUpperRange?: boolean) => {
        setTop5PercentEarner(isTopEarner);
        setDrilldownType(drilldownType);
        if (isUpperRange !== undefined) {
            setIsUpperRange(isUpperRange);
        }
        setPreviewPopup(true);
    }

    const handleCloseForm = () => {
        setPreviewPopup(false);
    }

    const getRecalculatedValues = async () => {
        saveAvgAndMedianForm();
        try {
            const dataResponse = await AverageAndMedianRemunerationService.RecalculateEarnerValues(props.scorecardId);
            getAvgAndMedianData(props.scorecardId);
        } catch (error: any) {
            console.log(error);
        }
    }

    const getAvgAndMedianData = async (scorecardId: string) => {
        try {
            const dataResponse = await AverageAndMedianRemunerationService.GetReport(scorecardId);
            const mappedBoolValues = {
                ...dataResponse.data,
                hasPolicyForverticalGap: dataResponse.data.hasPolicyForVerticalGap ? 1 : 0,
                isRemunerationGapAlignedToPolicy: dataResponse.data.isRemunerationGapAlignedToPolicy ? 1 : 0,
                hasAAMeasures: dataResponse.data.hasAAMeasures ? 1 : 0
            };
            setReasonForIncomeDifferentials(dataResponse.data.reasonForIncomeDifferentials);
            reset(mappedBoolValues);
        } catch (error: any) {
            console.log(error);
        }
    }

    const saveAvgAndMedianForm = async () => {
        const formData = getValues();
            const requestData: EEAverageAndMedianRemuneration.CalculatedFields = {
                ScorecardId: props.scorecardId,
                AvgTopEarners: formData.avgRemunerationOfTopEarners ? parseFloat(formData.avgRemunerationOfTopEarners.replace('R ', '')) : 0,
                AvgBottomEarners: formData.avgRemunerationOfBottomEarners ? parseFloat(formData.avgRemunerationOfBottomEarners.replace('R ', '')) : 0,
                MedianEarners: formData.medianEarners ? parseFloat(String(formData.medianEarners).replace('R ', '')) : 0
            }
        const response = await AverageAndMedianRemunerationService.SaveReport(requestData);
    }

    const onSubmit: SubmitHandler<any> = async(data) => {  
        try{
            const averageAndMedianRemuneration: EEAverageAndMedianRemuneration.UserCapturedFields = {
                ScorecardId: data.scorecardId,
                HasPolicyForVerticalGap: Boolean(data.hasPolicyForverticalGap),
                VerticalGapSize: data.verticalGapSize,
                IsRemunerationGapAlignedToPolicy: Boolean(data.isRemunerationGapAlignedToPolicy),
                HasAAMeasures: Boolean(data.hasAAMeasures),
                ReasonForIncomeDifferentials: data.reasonForIncomeDifferentials,
                OtherReasonForIncomeDifferentials: data.otherReasonForIncomeDifferentials
            }
            const result = await AverageAndMedianRemunerationService.SaveUserCapturedValues(averageAndMedianRemuneration);
            toast.success("Section updated successfully");                       
        }
        catch (error) {
            console.log(error)
        }
    };

    useEffect(() => {
        getAvgAndMedianData(props.scorecardId);
        
        return () => {
            if (localStorage.getItem('coreToken') !== null) {
                saveAvgAndMedianForm();
            }
        }
    }, [props.scorecardId]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box>
            <MDTypography variant='h5' fontWeight='bold' sx={{ mb: 1 }} >
                Median Remuneration and the Remuneration Gap
            </MDTypography>
            <Box mb={5}>
                <MDBox sx={{ border: '1px black solid', borderRadius: '12px' }} p={2}>

                    <Grid container mb={1}
                        justifyContent='flex-end'>
                        <MDTypography variant='h7' color='secondary' fontWeight='regular'>
                        </MDTypography>
                        <MDButton color='primary'
                            className="pull-right btn btn-bee-space-blue"
                            size="small"
                            onClick={() => { getRecalculatedValues() }}>
                            <RefreshIcon fontSize="medium" /> &nbsp; Recalculate Values

                        </MDButton>
                    </Grid>
                    <Grid container mb={1}
                        alignItems="center"
                        direction="row"
                        justifyContent="flex-start">
                        <Grid item xs={9}>
                            <MDTypography variant='h7' color='secondary'>
                                Number of employees covered in the top 5% of the highest paid employees in your organisation
                            </MDTypography>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="top5PercentHighestPaidEmployees"
                                defaultValue={""}
                                render={({ field: { value } }) => (
                                    <Button
                                        id="top5PercentHighestPaidEmployees"
                                        fullWidth
                                        onClick={() => handleClickTopOrBottom(true, RemunerationDrilldownType.Remuneration)}
                                        sx={ buttonTextLinkStyle }
                                    >
                                        { String(value || "").trim() || "0" }
                                    </Button>
                                )}
                            />
                        </Grid>
                    </Grid>                  

                    {/* --------------------------------------------------------------------------------------------------------------------------*/}

                    <Grid container mb={1}
                        alignItems="center"
                        direction="row"
                        justifyContent="flex-start">
                        <Grid item xs={9}>
                            <MDTypography variant='h7' color='secondary'>
                                What is the total annual remuneration of the top 5% of the highest paid employees in your organisation?
                            </MDTypography>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="top5PercentTotalAnnualRemuneration"
                                defaultValue={""}
                                render={({ field: { value } }) => (
                                    <Button
                                        id="top5PercentTotalAnnualRemuneration"
                                        fullWidth
                                        onClick={() => handleClickTopOrBottom(true, RemunerationDrilldownType.Remuneration)}
                                        sx={ buttonTextLinkStyle }
                                    >
                                        { CurrencyFormatter(value ?? "").trim() || "R0,00" } 
                                    </Button>
                                )}
                            />
                        </Grid>
                    </Grid>

                    {/* --------------------------------------------------------------------------------------------------------------------------*/}

                    <Grid container mb={1} alignItems="center" direction="row" justifyContent="flex-start">
                        <Grid item xs={9}>
                            <MDTypography variant='h7' color='secondary'>
                            What is the range of the top 5% of the highest paid employees in your organisation (total annual remuneration)?
                            </MDTypography>
                        </Grid>

                        <Grid item xs={3}>
                            <Grid container spacing={1}>
                                <Grid item xs={5.5}>
                                    <Controller
                                        control={control}
                                        name="top5PercentAnnualRemunerationMinimum"
                                        defaultValue=""
                                        render={({ field: { value } }) => (
                                            <Button
                                                id="top5PercentAnnualRemunerationMinimum"
                                                fullWidth
                                                onClick={() => handleClickTopOrBottom(true, RemunerationDrilldownType.Range, false)}
                                                sx={ buttonTextLinkStyle }
                                            >
                                                { CurrencyFormatter(value ?? "").trim() || "R0,00" } 
                                            </Button>
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={1} style={{ textAlign: 'center' }}>
                                    <MDTypography variant="h7">–</MDTypography>
                                </Grid>

                                <Grid item xs={5.5}>
                                    <Controller
                                        control={control}
                                        name="top5PercentAnnualRemunerationMaximum"
                                        defaultValue=""
                                        render={({ field: { value } }) => (
                                            <Button
                                                id="top5PercentAnnualRemunerationMaximum"
                                                fullWidth
                                                onClick={() => handleClickTopOrBottom(true, RemunerationDrilldownType.Range, true)}
                                                sx={ buttonTextLinkStyle }
                                            >
                                                { CurrencyFormatter(value ?? "").trim() || "R0,00" } 
                                            </Button>
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* --------------------------------------------------------------------------------------------------------------------------*/}

                    <Grid container mb={1}
                        alignItems="center"
                        direction="row"
                        justifyContent="flex-start">
                        <Grid item xs={9}>
                            <MDTypography variant='h7' color='secondary'>
                                Number of employees covered in the lowest 5% paid employees in your organisation
                            </MDTypography>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="bottom5PercentLowestPaidEmployees"
                                defaultValue={""}
                                render={({ field: { value } }) => (
                                    <Button
                                        id="bottom5PercentLowestPaidEmployees"
                                        fullWidth
                                        onClick={() => handleClickTopOrBottom(false, RemunerationDrilldownType.Remuneration)}
                                        sx={ buttonTextLinkStyle }
                                    >
                                        { String(value || "").trim() || "0" }
                                    </Button>
                                )}
                            />
                        </Grid>
                    </Grid>

                    {/* --------------------------------------------------------------------------------------------------------------------------*/}

                    <Grid container mb={1}
                        alignItems="center"
                        direction="row"
                        justifyContent="flex-start">
                        <Grid item xs={9}>
                            <MDTypography variant='h7' color='secondary'>
                                What is the total annual remuneration of the lowest 5% paid employees in your organisation?
                            </MDTypography>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="bottom5PercentTotalAnnualRemuneration"
                                defaultValue={""}
                                render={({ field: { value } }) => (
                                    <Button
                                        id="bottom5PercentTotalAnnualRemuneration"
                                        fullWidth
                                        onClick={() => handleClickTopOrBottom(false, RemunerationDrilldownType.Remuneration)}
                                        sx={ buttonTextLinkStyle }
                                    >
                                        { CurrencyFormatter(value ?? "").trim() || "R0,00" } 
                                    </Button>
                                )}
                            />
                        </Grid>
                    </Grid>

                    {/* --------------------------------------------------------------------------------------------------------------------------*/}

                    <Grid container mb={1} alignItems="center" direction="row" justifyContent="flex-start">
                        <Grid item xs={9}>
                            <MDTypography variant='h7' color='secondary'>
                                What is the range of the lowest 5% paid employees in your organisation (total annual remuneration)?
                            </MDTypography>
                        </Grid>

                        <Grid item xs={3}>
                            <Grid container spacing={1}>
                                <Grid item xs={5.5}>
                                    <Controller
                                        control={control}
                                        name="bottom5PercentAnnualRemunerationMinimum"
                                        defaultValue=""
                                        render={({ field: { value } }) => (
                                            <Button
                                                id="bottom5PercentAnnualRemunerationMinimum"
                                                fullWidth
                                                onClick={() => handleClickTopOrBottom(false, RemunerationDrilldownType.Range, false)}
                                                sx={ buttonTextLinkStyle }
                                            >
                                                { CurrencyFormatter(value ?? "").trim() || "R0,00" } 
                                            </Button>
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={1} style={{ textAlign: 'center' }}>
                                    <MDTypography variant="h7">–</MDTypography>
                                </Grid>
                                
                                <Grid item xs={5.5}>
                                    <Controller
                                        control={control}
                                        name="bottom5PercentAnnualRemunerationMaximum"
                                        defaultValue=""
                                        render={({ field: { value } }) => (
                                        <Button
                                                id="bottom5PercentAnnualRemunerationMaximum"
                                                fullWidth
                                                onClick={() => handleClickTopOrBottom(false, RemunerationDrilldownType.Range, true)}
                                                sx={ buttonTextLinkStyle }
                                            >
                                                { CurrencyFormatter(value ?? "").trim() || "R0,00" } 
                                            </Button>
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    
                    <MDTypography
                        variant="button"
                        fontWeight="regular"
                        style={{ color: '#FF0000', fontSize: "0.6em" }}
                    >
                       Employees with R0 annual salaries are included with the Bottom Earners calculation
                    </MDTypography>

                    {/* --------------------------------------------------------------------------------------------------------------------------*/}
                   
                    <Grid container mb={1}
                        alignItems="center"
                        direction="row"
                        justifyContent="flex-start">
                        <Grid item xs={9}>
                            <MDTypography variant='h7' color='secondary'>
                                What is the median earners remuneration in your organisation?
                            </MDTypography>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="medianEarners"
                                defaultValue={""}
                                render={({ field: { value } }) => (
                                     <Button
                                            id="medianEarners"
                                            fullWidth
                                            onClick={() => handleClickTopOrBottom(true, RemunerationDrilldownType.Median)}
                                            sx={ buttonTextLinkStyle }
                                        >
                                            { CurrencyFormatter(value ?? "").trim() || "R0,00" } 
                                        </Button>
                                )}
                            />
                        </Grid>
                    </Grid>
                </MDBox>

                <br />

                <form id="UserCapturedValuesForm" onSubmit={handleSubmit(onSubmit)}>
                    <MDBox sx={{ border: '1px black solid', borderRadius: '12px' }} p={2}>
                        <Grid container mb={1}
                            alignItems="center"
                            direction="row"
                            justifyContent="flex-start">
                            <Grid item xs={8}>
                                <MDTypography variant='h7' color='secondary'>Please indicate whether your organisation has a policy in place to address and close the vertical gap  between the highest and lowest paid employees in your workforce?</MDTypography>
                            </Grid>
                            <Grid xs={1}></Grid>
                            <Grid item xs={3}>
                                <AppSelectList control={control} name="hasPolicyForverticalGap" label="" items={YesNoList} size='small'/>
                            </Grid>
                        </Grid>

                        {/* --------------------------------------------------------------------------------------------------------------------------*/}

                        <br />
                        <Grid container mb={1}
                            alignItems="center"
                            direction="row"
                            justifyContent="flex-start">
                            <Grid item xs={8}>
                                <MDTypography variant='h7' color='secondary'>
                                    How many times (e.g.  10x, 15x, 20x) is the vertical gap between the highest and lowest paid worker in your organisation in terms of the policy?
                                </MDTypography>
                            </Grid>
                            <Grid xs={1}></Grid>
                            <Grid item xs={3}>
                                <AppTextInput control={control} name="verticalGapSize" label="" size='small' disabled={true} />
                            </Grid>
                        </Grid>

                        {/* --------------------------------------------------------------------------------------------------------------------------*/}

                        <br />
                        <Grid container mb={1}
                            alignItems="center"
                            direction="row"
                            justifyContent="flex-start">
                            <Grid item xs={8}>
                                <MDTypography variant='h7' color='secondary'>Is the remuneration-gap between the highest and lowest paid employees in your organisation aligned to your policy?</MDTypography>
                            </Grid>
                            <Grid xs={1}></Grid>
                            <Grid item xs={3}>
                                <AppSelectList control={control} name="isRemunerationGapAlignedToPolicy" label="" items={YesNoList} size='small'/>
                            </Grid>
                        </Grid>
                        
                        {/* --------------------------------------------------------------------------------------------------------------------------*/}

                        <Grid container mb={1}
                            alignItems="center"
                            direction="row"
                            justifyContent="flex-start">
                            <Grid item xs={9}>
                                <MDTypography variant='h7' color='secondary'>Are AA measures to address the remuneration gap included in your EE Plan?</MDTypography>
                            </Grid>
                            <Grid item xs={3}>
                                <AppSelectList control={control} name="hasAAMeasures" label="" items={YesNoList} size='small'/>
                            </Grid>
                        </Grid>

                        {/* --------------------------------------------------------------------------------------------------------------------------*/}

                        <Grid container mb={1}
                            alignItems="center"
                            direction="row"
                            justifyContent="flex-start">
                            <Grid item xs={9}>
                                <MDTypography variant='h7' color='secondary'>Please indicate a key reason for the Income Differentials that apply to your organisation. </MDTypography>
                            </Grid>
                            <Grid item xs={3}>
                                <AppSelectList 
                                    control={control} 
                                    name="reasonForIncomeDifferentials" 
                                    label="" 
                                    items={incomeDifferentialsOptions} 
                                    size='small' 
                                    onChange={(value) => {                                  
                                        setReasonForIncomeDifferentials(value);
                                        if (value !== 8) {
                                            setValue('otherReasonForIncomeDifferentials', '');
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>

                        {reasonForIncomeDifferentials === 8 && 
                            <Grid container mb={1}
                                alignItems="center"
                                direction="row"
                                justifyContent="flex-start">
                                <Grid item xs={9}>
                                    <MDTypography variant='h7' color='secondary'>Please capture the specific key reason.</MDTypography>
                                </Grid>
                                <Grid item xs={3}>
                                    <AppTextInput control={control} name="otherReasonForIncomeDifferentials" label="" />
                                </Grid>
                            </Grid>
                        }
                        <Grid container mb={1}
                                alignItems="center"
                                direction="row"
                                justifyContent="flex-start">
                                <Grid item xs={9}>
                                </Grid>
                                <Grid item xs={3}>
                                    <MDButton color='primary' className="pull-right btn btn-bee-space-blue" size="small" type="submit" form='UserCapturedValuesForm'>
                                        Save
                                    </MDButton>
                                </Grid>
                            </Grid>

                    </MDBox>
                </form>

            </Box >
            <Dialog open={previewPopup} onClose={handleCloseForm} aria-labelledby="form-dialog-title" maxWidth='xl' fullWidth>
                <CustomDialogTitle id="customized-dialog-title" onClose={handleCloseForm}>

                </CustomDialogTitle>
                <DialogContent>
                    <MedianRemunerationEarnerPopup getTop5Percent={isTop5PercentEarner} drilldownType={drilldownType} isUpperRange={isUpperRange}/>
                </DialogContent>
            </Dialog>
        </Box >
    )
}
